// IndividualInvestorDashboard.js - Individual Investor Dashboard
// URL-path-based tabs, zinc/white/monospace Tailwind design, inline demo data
// Simplified settings (no entity tree, no multi-sig), guided dealing mode

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Settings, Target, DollarSign, ArrowRightLeft, TrendingUp, Wallet, Briefcase,
  Globe, Building2, MessageSquare, Search, RefreshCw, CheckCircle, Clock,
  AlertTriangle, Eye, FileText, BarChart3, Calendar, Percent, CreditCard,
  Shield, Bell, LineChart, ExternalLink
} from 'lucide-react';
import { getStatusStyle } from '../../config/statusColors';
import { useAuth } from '../../context/AuthContext';
import ProjectionsPage from '../projections/ProjectionsPage';
import PortfolioPage from '../portfolio/PortfolioPage';
import YieldDifferentialExplorer from '../common/YieldDifferentialExplorer';
import RatesBoard from '../rates/RatesBoard';
import ComplianceTab from '../compliance/ComplianceTab';
import FxOverlayTab from '../portfolio/FxOverlayTab';
import LiquidityTab from '../liquidity/LiquidityTab';
import usePortfolioData from '../../hooks/usePortfolioData';
import { validateTicket } from '../../lib/dealing';
import { csdGateway } from '../../services/csdGateway';
import { validateTransition, TRANSACTION_STATES } from '../../lib/transactionLifecycle';
import { UPCOMING_AUCTIONS, AUCTION_RULES_KENYA } from '../../data/dealingMockData';

// ============================================
// DEMO / FALLBACK DATA
// ============================================
const demoData = {
  markets: [
    { market: 'Kenya', tenor: '91-day', rate: 16.25, lastAuction: '2026-09-04', currency: 'KES' },
    { market: 'Kenya', tenor: '182-day', rate: 16.48, lastAuction: '2026-09-04', currency: 'KES' },
    { market: 'Kenya', tenor: '364-day', rate: 16.72, lastAuction: '2026-09-04', currency: 'KES' },
    { market: 'Uganda', tenor: '91-day', rate: 14.30, lastAuction: '2026-09-02', currency: 'UGX' },
    { market: 'Uganda', tenor: '182-day', rate: 14.55, lastAuction: '2026-09-02', currency: 'UGX' },
    { market: 'Uganda', tenor: '364-day', rate: 14.89, lastAuction: '2026-09-02', currency: 'UGX' },
    { market: 'Tanzania', tenor: '91-day', rate: 9.10, lastAuction: '2026-08-28', currency: 'TZS' },
    { market: 'Tanzania', tenor: '182-day', rate: 9.45, lastAuction: '2026-08-28', currency: 'TZS' },
    { market: 'Nigeria', tenor: '91-day', rate: 18.50, lastAuction: '2026-09-06', currency: 'NGN' },
    { market: 'Nigeria', tenor: '364-day', rate: 19.20, lastAuction: '2026-09-06', currency: 'NGN' },
  ],
  auctionCalendar: [
    { market: 'Kenya', date: '2026-09-18', tenor: '91-day / 182-day', status: 'upcoming' },
    { market: 'Uganda', date: '2026-09-16', tenor: '91-day / 182-day / 364-day', status: 'upcoming' },
    { market: 'Tanzania', date: '2026-09-25', tenor: '91-day / 182-day', status: 'upcoming' },
    { market: 'Nigeria', date: '2026-09-20', tenor: '91-day / 364-day', status: 'upcoming' },
    { market: 'Kenya', date: '2026-10-02', tenor: '364-day', status: 'upcoming' },
  ],
  fxRfqs: [
    { id: 'FXR-2001', pair: 'USD/KES', amount: 50000, direction: 'Buy KES', banksInvited: 3, bestRate: 129.85, status: 'open', createdAt: '2026-09-12T09:00:00Z' },
    { id: 'FXR-2002', pair: 'EUR/KES', amount: 30000, direction: 'Buy KES', banksInvited: 4, bestRate: 142.10, status: 'completed', createdAt: '2026-09-10T14:30:00Z' },
    { id: 'FXR-2003', pair: 'GBP/UGX', amount: 20000, direction: 'Buy UGX', banksInvited: 2, bestRate: null, status: 'pending', createdAt: '2026-09-12T11:15:00Z' },
  ],
  forwardsPool: [
    { id: 'FWD-1001', pair: 'USD/KES', notional: 50000, tenor: '3M', divestmentRate: 130.25, status: 'active', maturity: '2026-12-15' },
    { id: 'FWD-1002', pair: 'EUR/KES', tenor: '6M', notional: 30000, divestmentRate: null, status: 'pending', maturity: '2027-03-10' },
  ],
  forwardCurve: [
    { tenor: 'Spot', rate: 129.50 },
    { tenor: '1M', rate: 129.85 },
    { tenor: '3M', rate: 130.25 },
    { tenor: '6M', rate: 131.10 },
    { tenor: '9M', rate: 131.80 },
    { tenor: '12M', rate: 132.60 },
  ],
  settlements: [
    { id: 'SET-3001', instrument: 'KE-TB-91D-SEP', faceValue: 50000, fxRate: 129.85, fees: 125.00, total: 49875.00, status: 'pending_settlement', date: '2026-09-15' },
    { id: 'SET-3002', instrument: 'UG-TB-182D-AUG', faceValue: 30000, fxRate: 3720.50, fees: 75.00, total: 29925.00, status: 'completed', date: '2026-09-10' },
    { id: 'SET-3003', instrument: 'KE-TB-364D-SEP', faceValue: 80000, fxRate: 129.90, fees: 200.00, total: 79800.00, status: 'pending_settlement', date: '2026-09-18' },
  ],
  holdings: [
    { id: 'HLD-001', instrument: 'KE-TB-91D-2026Q3', market: 'Kenya', tenor: '91-day', faceValue: 50000, yieldRate: 16.25, purchaseDate: '2026-09-04', maturityDate: '2026-12-04', status: 'active' },
    { id: 'HLD-002', instrument: 'UG-TB-182D-2026Q3', market: 'Uganda', tenor: '182-day', faceValue: 30000, yieldRate: 14.55, purchaseDate: '2026-09-02', maturityDate: '2027-03-03', status: 'active' },
    { id: 'HLD-003', instrument: 'KE-TB-364D-2026Q2', market: 'Kenya', tenor: '364-day', faceValue: 80000, yieldRate: 16.72, purchaseDate: '2026-06-15', maturityDate: '2027-06-14', status: 'active' },
    { id: 'HLD-004', instrument: 'TZ-TB-91D-2026Q2', market: 'Tanzania', tenor: '91-day', faceValue: 25000, yieldRate: 9.10, purchaseDate: '2026-06-28', maturityDate: '2026-09-27', status: 'maturing' },
    { id: 'HLD-005', instrument: 'NG-TB-91D-2026Q3', market: 'Nigeria', tenor: '91-day', faceValue: 40000, yieldRate: 18.50, purchaseDate: '2026-09-06', maturityDate: '2026-12-06', status: 'active' },
  ],
  secondaryListings: [
    { id: 'SEC-401', instrument: 'KE-TB-364D-2026Q1', faceValue: 60000, askPrice: 58200, yieldImplied: 17.10, listedDate: '2026-09-08', status: 'active' },
    { id: 'SEC-402', instrument: 'UG-TB-182D-2026Q2', faceValue: 25000, askPrice: 24100, yieldImplied: 15.20, listedDate: '2026-09-05', status: 'active' },
  ],
  secondaryOffers: [
    { id: 'OFF-501', listingId: 'SEC-401', bidPrice: 57800, bidder: 'Anonymous', status: 'pending', date: '2026-09-10' },
  ],
  kycStatus: [
    { bank: 'Equity Bank', kycStatus: 'verified', cdsCard: 'active', lastUpdated: '2026-08-15' },
    { bank: 'NCBA Bank', kycStatus: 'pending_kyc', cdsCard: 'pending', lastUpdated: '2026-09-01' },
    { bank: 'Stanbic Bank', kycStatus: 'verified', cdsCard: 'active', lastUpdated: '2026-07-20' },
  ],
  documents: [
    { name: 'Passport Copy', type: 'ID', status: 'verified', uploadedAt: '2026-06-10' },
    { name: 'Proof of Address', type: 'Address', status: 'verified', uploadedAt: '2026-06-10' },
    { name: 'Tax Compliance Certificate', type: 'Tax', status: 'expiring', uploadedAt: '2026-03-15' },
    { name: 'Source of Funds Declaration', type: 'AML', status: 'verified', uploadedAt: '2026-06-10' },
  ],
  messages: [
    { id: 'MSG-101', title: 'Bid Allocated', body: 'Your bid for KE-TB-91D has been allocated at 16.25%.', type: 'lifecycle', read: false, date: '2026-09-12T10:00:00Z' },
    { id: 'MSG-102', title: 'Settlement Due', body: 'Settlement for SET-3001 is due on 15 Sep 2026. Please ensure funds are available.', type: 'settlement', read: false, date: '2026-09-12T08:30:00Z' },
    { id: 'MSG-103', title: 'Foreign Exchange Quote Received', body: 'Best rate for FXR-2001 (USD/KES): 129.85 from 3 banks.', type: 'fx', read: true, date: '2026-09-11T16:45:00Z' },
    { id: 'MSG-104', title: 'Maturity Notice', body: 'HLD-004 (TZ-TB-91D) matures on 27 Sep 2026. Choose rollover or realise.', type: 'maturity', read: false, date: '2026-09-11T09:00:00Z' },
    { id: 'MSG-105', title: 'KYC Update Required', body: 'NCBA Bank requires updated KYC documentation. Please upload by 30 Sep 2026.', type: 'kyc', read: true, date: '2026-09-09T11:20:00Z' },
    { id: 'MSG-106', title: 'Forward Confirmed', body: 'FWD-1001 (USD/KES 3M) confirmed at divestment rate 130.25.', type: 'lifecycle', read: true, date: '2026-09-08T14:10:00Z' },
    { id: 'MSG-107', title: 'Secondary Offer Received', body: 'New bid of $57,800 received on SEC-401.', type: 'secondary', read: false, date: '2026-09-10T13:00:00Z' },
  ],
};

// ============================================
// FORMAT HELPERS
// ============================================
const formatCurrency = (val) => {
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
  return `$${val?.toLocaleString() || 0}`;
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '\u2014';
const formatTime = (d) => d ? new Date(d).toLocaleString() : '\u2014';

// ============================================
// STATUS BADGE HELPER
// ============================================
const getStatusBadge = (status) => {
  const style = getStatusStyle(status);
  return {
    className: 'inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border rounded-none',
    style: { ...style, borderColor: style.color },
  };
};

// ============================================
// STAT CARD COMPONENT
// ============================================
const StatCard = ({ label, value, icon: Icon, trend }) => (
  <div className="bg-white border border-zinc-200 rounded-none p-6">
    <div className="flex items-center justify-between mb-2">
      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{label}</span>
      <Icon className="w-4 h-4 text-zinc-400" />
    </div>
    <p className="text-2xl font-bold text-black">{value}</p>
    {trend && <p className="text-xs text-zinc-500 mt-1">{trend}</p>}
  </div>
);

// ============================================
// TOGGLE COMPONENT
// ============================================
const Toggle = ({ enabled, onChange }) => (
  <button
    onClick={onChange}
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${enabled ? 'bg-zinc-700' : 'bg-zinc-300'}`}
  >
    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
  </button>
);

// ============================================
// URL PATH TO TAB MAPPING
// ============================================
const getTabFromPath = (pathname) => {
  // Pillar paths
  if (pathname.includes('/investor/yield')) return 'markets';
  if (pathname.includes('/investor/rates')) return 'fx';
  if (pathname.includes('/investor/compliance')) return 'compliance';
  if (pathname.includes('/investor/hedging')) return 'forwards_market';
  if (pathname.includes('/investor/liquidity')) return 'secondary_market';
  if (pathname.includes('/investor/execution')) return 'dealing_room';
  // Legacy paths (backward compat)
  if (pathname.includes('/investor/settings')) return 'settings';
  if (pathname.includes('/investor/markets')) return 'markets';
  if (pathname.includes('/investor/dealing')) return 'dealing_room';
  if (pathname.includes('/investor/fx')) return 'fx';
  if (pathname.includes('/investor/forwards')) return 'forwards_market';
  if (pathname.includes('/investor/settlement')) return 'settlement';
  if (pathname.includes('/investor/portfolio')) return 'portfolio';
  if (pathname.includes('/investor/secondary')) return 'secondary_market';
  if (pathname.includes('/investor/projections')) return 'projections';
  if (pathname.includes('/investor/profile')) return 'profile';
  if (pathname.includes('/investor/messages')) return 'messages';
  return 'settings';
};

// ============================================
// MAIN INDIVIDUAL INVESTOR DASHBOARD
// ============================================
const IndividualInvestorDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { positions: portfolioPositions, fxHedges } = usePortfolioData();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Settings state (simplified for individual)
  const [notifPrefs, setNotifPrefs] = useState({ email: true, sms: false, push: true });
  const [displayCurrency, setDisplayCurrency] = useState('USD');
  const [language, setLanguage] = useState('en');

  // Dealing wizard state
  const [dealingStep, setDealingStep] = useState(1);
  const [dealingForm, setDealingForm] = useState({ market: '', instrument: '', amount: '', rate: '' });

  // Settlement agreement state
  const [settlementAgreed, setSettlementAgreed] = useState({});

  // Bid submission state
  const [bidSubmitting, setBidSubmitting] = useState(false);
  const [bidError, setBidError] = useState(null);
  const [bidSuccess, setBidSuccess] = useState(null);

  // Settlement lifecycle state
  const [settlementStatuses, setSettlementStatuses] = useState({});
  const [settleSubmitting, setSettleSubmitting] = useState(null);

  // Demo lifecycle driver
  const [demoLifecycle, setDemoLifecycle] = useState(null);

  // Determine active tab from URL path
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));

  // Update tab when URL changes
  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  // Simulate initial load
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 400);
    return () => clearTimeout(timer);
  }, []);

  // Demo lifecycle steps
  const DEMO_LIFECYCLE_STEPS = [
    { state: 'bid_placed', label: 'Bid Placed', delay: 0 },
    { state: 'submitted_to_csd', label: 'Submitted to CSD', delay: 2000 },
    { state: 'bid_allocated', label: 'Bid Allocated', delay: 3500 },
    { state: 'settlement_pending', label: 'Settlement Pending', delay: 5000 },
    { state: 'fx_rate_locked', label: 'FX Rate Locked', delay: 6500 },
    { state: 'csd_confirmed', label: 'CSD Confirmed', delay: 8000 },
    { state: 'portfolio_active', label: 'Portfolio Active', delay: 9500 },
  ];

  useEffect(() => {
    if (!demoLifecycle) return;
    const timers = DEMO_LIFECYCLE_STEPS.slice(1).map((step, i) =>
      setTimeout(() => {
        setDemoLifecycle(prev => prev ? { ...prev, currentStep: i + 1, state: step.state } : null);
      }, step.delay)
    );
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoLifecycle?.csdRef]);

  // ============================================
  // TABS CONFIGURATION
  // ============================================
  const tabs = [
    { id: 'settings', label: 'Overview', path: '/investor', icon: Settings },
    { id: 'markets', label: 'Yield Discovery', path: '/investor/yield', icon: Target },
    { id: 'fx', label: 'Rates & FX', path: '/investor/rates', icon: ArrowRightLeft },
    { id: 'compliance', label: 'Compliance', path: '/investor/compliance', icon: Shield },
    { id: 'forwards_market', label: 'Hedging', path: '/investor/hedging', icon: Globe },
    { id: 'dealing_room', label: 'Execution', path: '/investor/execution', icon: DollarSign },
    { id: 'settlement', label: 'Settlement', path: '/investor/settlement', icon: CheckCircle },
    { id: 'portfolio', label: 'Portfolio', path: '/investor/portfolio', icon: Briefcase },
    { id: 'secondary_market', label: 'Liquidity', path: '/investor/secondary', icon: Building2 },
    { id: 'projections', label: 'Projections', path: '/investor/projections', icon: LineChart },
    { id: 'profile', label: 'Profile', path: '/investor/profile', icon: Shield },
    { id: 'messages', label: 'Messages', path: '/investor/messages', icon: MessageSquare },
  ];

  // Computed portfolio summaries
  const totalValue = demoData.holdings.reduce((s, h) => s + h.faceValue, 0);
  const weightedYield = demoData.holdings.length > 0
    ? (demoData.holdings.reduce((s, h) => s + h.yieldRate * h.faceValue, 0) / totalValue).toFixed(2)
    : 0;
  const nextMaturity = demoData.holdings
    .filter(h => h.status === 'active' || h.status === 'maturing')
    .sort((a, b) => new Date(a.maturityDate) - new Date(b.maturityDate))[0];
  const unreadMessages = demoData.messages.filter(m => !m.read).length;

  const handleRefresh = async () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 600);
  };

  // ─── Submit Bid Handler (Wizard Step 4) ──────────────────────────
  const handleSubmitBid = async () => {
    setBidError(null);
    setBidSuccess(null);

    if (!dealingForm.market) { setBidError('Please select a market.'); return; }
    if (!dealingForm.amount || Number(dealingForm.amount) <= 0) { setBidError('Please enter a valid amount.'); return; }

    // Find matching auction for the selected market
    const auction = UPCOMING_AUCTIONS.find(a => {
      const marketMap = { Kenya: 'KE', Uganda: 'UG', Tanzania: 'TZ', Nigeria: 'NG' };
      return a.market === marketMap[dealingForm.market];
    });
    if (!auction) { setBidError('No matching auction found for this market.'); return; }

    const amount = Number(dealingForm.amount);
    const rate = dealingForm.rate ? Number(dealingForm.rate) : null;
    const isCompetitive = rate && rate > 0;

    const ticket = {
      id: `TKT-IND-${Date.now()}`,
      entityId: 'ENT-003',
      auctionId: auction.id,
      status: 'draft',
      tranches: [{ type: isCompetitive ? 'competitive' : 'non_competitive', amount, yield: rate }],
      fundingRoute: 'RTGS',
      createdAt: new Date().toISOString(),
    };

    const validation = validateTicket(ticket, auction);
    if (!validation.valid) {
      setBidError(validation.errors.join('; '));
      return;
    }

    setBidSubmitting(true);
    try {
      const result = await csdGateway.KE.submitBid(ticket);
      if (result.success) {
        setBidSuccess(`Bid submitted — CSD Ref: ${result.csdRef}`);
        setDealingStep(1);
        setDealingForm({ market: '', instrument: '', amount: '', rate: '' });
        setDemoLifecycle({ csdRef: result.csdRef, currentStep: 0, state: 'bid_placed', startedAt: Date.now() });
      } else {
        setBidError('CSD submission failed. Please retry.');
      }
    } catch (err) {
      setBidError(err.message || 'Unexpected error during bid submission.');
    } finally {
      setBidSubmitting(false);
    }
  };

  // ─── Settle Handler ──────────────────────────────────────────────
  const handleSettle = async (set) => {
    setSettleSubmitting(set.id);
    const currentState = TRANSACTION_STATES.SETTLEMENT_PENDING;
    const targetState = TRANSACTION_STATES.FX_RFQ_SENT;
    const role = 'investor_individual';

    const check = validateTransition(currentState, targetState, role);
    if (!check.valid) {
      alert(`Settlement transition blocked: ${check.reason}`);
      setSettleSubmitting(null);
      return;
    }

    await new Promise(r => setTimeout(r, 300));
    setSettlementStatuses(prev => ({ ...prev, [set.id]: 'completed' }));
    setSettleSubmitting(null);
  };

  // ============================================
  // LOADING STATE
  // ============================================
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-zinc-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-zinc-600 mt-4">Loading investor dashboard...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="p-6 bg-zinc-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-zinc-600 to-zinc-800 rounded-none p-6 text-white mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="w-6 h-6" />
              <span className="text-zinc-200 text-sm font-medium">INDIVIDUAL INVESTOR</span>
            </div>
            <h1 className="text-2xl font-bold">{user?.name || 'My Investment Account'}</h1>
            <p className="text-zinc-100 mt-1">
              {demoData.holdings.length} holdings &bull; {unreadMessages} unread messages
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-zinc-200">Total Portfolio</p>
            <p className="text-3xl font-bold">{formatCurrency(totalValue)}</p>
            <p className="text-sm text-zinc-200 mt-1">Weighted Yield: {weightedYield}%</p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2 mb-6 bg-white rounded-none p-1 shadow-sm overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => navigate(tab.path)}
            className={`flex items-center gap-2 px-4 py-2 rounded-none font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id ? 'bg-zinc-600 text-white' : 'text-zinc-600 hover:bg-zinc-100'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
        <div className="flex-1" />
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 text-zinc-600 hover:bg-zinc-100 rounded-none"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* ============================================ */}
      {/* SETTINGS TAB (simplified)                    */}
      {/* ============================================ */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <StatCard label="Display Currency" value={displayCurrency} icon={DollarSign} trend="Preferred currency" />
            <StatCard label="Language" value={language === 'en' ? 'English' : language} icon={Globe} trend="Interface language" />
            <StatCard label="Notifications" value={Object.values(notifPrefs).filter(Boolean).length + ' active'} icon={Bell} trend="Channels enabled" />
          </div>

          {/* Notification Preferences */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Notification Preferences</h3>
            <div className="space-y-4">
              {[
                { key: 'email', label: 'Email Notifications', desc: 'Receive trade confirmations, settlement alerts, and maturity notices via email' },
                { key: 'sms', label: 'SMS Notifications', desc: 'Critical alerts sent to your registered mobile number' },
                { key: 'push', label: 'Push Notifications', desc: 'In-app notifications for real-time updates on deals and quotes' },
              ].map(pref => (
                <div key={pref.key} className="flex items-center justify-between py-3 border-b border-zinc-100 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-zinc-900">{pref.label}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{pref.desc}</p>
                  </div>
                  <Toggle
                    enabled={notifPrefs[pref.key]}
                    onChange={() => setNotifPrefs(prev => ({ ...prev, [pref.key]: !prev[pref.key] }))}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Display Currency */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Display Currency</h3>
            <p className="text-xs text-zinc-500 mb-3">All portfolio values and summaries will be displayed in your selected currency.</p>
            <div className="flex flex-wrap gap-2">
              {['USD', 'EUR', 'GBP', 'KES', 'UGX', 'TZS', 'NGN'].map(cur => (
                <button
                  key={cur}
                  onClick={() => setDisplayCurrency(cur)}
                  className={`px-4 py-2 border text-sm font-mono rounded-none transition-colors ${
                    displayCurrency === cur
                      ? 'bg-zinc-700 text-white border-zinc-700'
                      : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50'
                  }`}
                >
                  {cur}
                </button>
              ))}
            </div>
          </div>

          {/* Language */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Language</h3>
            <p className="text-xs text-zinc-500 mb-3">Select your preferred interface language.</p>
            <div className="flex flex-wrap gap-2">
              {[
                { code: 'en', label: 'English' },
                { code: 'fr', label: 'French' },
                { code: 'sw', label: 'Swahili' },
                { code: 'pt', label: 'Portuguese' },
              ].map(lang => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={`px-4 py-2 border text-sm font-mono rounded-none transition-colors ${
                    language === lang.code
                      ? 'bg-zinc-700 text-white border-zinc-700'
                      : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>

          {/* Trading Facilities */}
          <div>
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Trading Facilities</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div
                onClick={() => navigate('/investor/fx')}
                className="bg-white border border-zinc-200 rounded-none p-6 hover:shadow-md transition-shadow cursor-pointer group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 bg-zinc-100 rounded-none flex items-center justify-center group-hover:bg-zinc-200 transition-colors">
                    <ArrowRightLeft className="w-5 h-5 text-zinc-600" />
                  </div>
                  <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-[10px] font-mono uppercase tracking-wider rounded">Use anytime</span>
                </div>
                <h4 className="font-semibold text-sm text-black mb-1">Foreign Exchange</h4>
                <p className="text-xs text-zinc-500">Request competitive spot FX quotes from multiple partner banks</p>
              </div>
              <div
                onClick={() => navigate('/investor/forwards')}
                className="bg-white border border-zinc-200 rounded-none p-6 hover:shadow-md transition-shadow cursor-pointer group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 bg-zinc-100 rounded-none flex items-center justify-center group-hover:bg-zinc-200 transition-colors">
                    <TrendingUp className="w-5 h-5 text-zinc-600" />
                  </div>
                  <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-[10px] font-mono uppercase tracking-wider rounded">Use anytime</span>
                </div>
                <h4 className="font-semibold text-sm text-black mb-1">Forwards Contracts</h4>
                <p className="text-xs text-zinc-500">Buy forward contracts competitively from multiple partner banks</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MARKETS TAB                                  */}
      {/* ============================================ */}
      {activeTab === 'markets' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard label="Markets Tracked" value="4" icon={Globe} trend="Kenya, Uganda, Tanzania, Nigeria" />
            <StatCard label="Best 91-day Rate" value="18.50%" icon={TrendingUp} trend="Nigeria" />
            <StatCard label="Upcoming Auctions" value={demoData.auctionCalendar.length} icon={Calendar} trend="Next 30 days" />
            <StatCard label="Instruments" value={demoData.markets.length} icon={BarChart3} trend="Active tenors" />
          </div>

          {/* Yield Differential Explorer */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Yield Differential Explorer</h3>
              <a
                href="/simulator"
                className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-200 rounded-none text-[10px] font-mono uppercase tracking-[0.12em] text-zinc-600 hover:bg-zinc-50 transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                Open in Simulator
              </a>
            </div>
            <YieldDifferentialExplorer />
          </div>

          {/* Market Rates Table */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">T-Bill Rates by Market</h3>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Search markets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 border border-zinc-200 rounded-none text-sm focus:outline-none focus:border-zinc-400"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Market</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Tenor</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Rate (%)</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Last Auction</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Currency</th>
                  </tr>
                </thead>
                <tbody>
                  {demoData.markets
                    .filter(m => !searchTerm || m.market.toLowerCase().includes(searchTerm.toLowerCase()) || m.tenor.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((m, i) => (
                    <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 font-mono text-xs font-bold">{m.market}</td>
                      <td className="py-3 px-4 font-mono text-xs">{m.tenor}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs font-bold">{m.rate.toFixed(2)}</td>
                      <td className="py-3 px-4 text-xs text-zinc-500">{formatDate(m.lastAuction)}</td>
                      <td className="py-3 px-4 font-mono text-xs text-zinc-500">{m.currency}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Auction Calendar */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Upcoming Auction Calendar</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Market</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Date</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Tenors</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {demoData.auctionCalendar.map((a, i) => (
                    <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 font-mono text-xs font-bold">{a.market}</td>
                      <td className="py-3 px-4 text-xs">{formatDate(a.date)}</td>
                      <td className="py-3 px-4 font-mono text-xs text-zinc-500">{a.tenor}</td>
                      <td className="py-3 px-4">
                        <span {...getStatusBadge(a.status)}>{a.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* DEALING ROOM TAB (guided wizard)             */}
      {/* ============================================ */}
      {activeTab === 'dealing_room' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard label="Current Step" value={`${dealingStep} of 4`} icon={Target} trend="Guided dealing mode" />
            <StatCard label="Markets Available" value="4" icon={Globe} trend="Select a market" />
            <StatCard label="Your Holdings" value={demoData.holdings.length} icon={Briefcase} trend="Active investments" />
            <StatCard label="Pending Deals" value="1" icon={Clock} trend="Awaiting settlement" />
          </div>

          {/* Step Indicator */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Deal Wizard</h3>
            <div className="flex items-center gap-2 mb-6">
              {['Select Market', 'Choose Instrument', 'Amount & Rate', 'Review & Submit'].map((step, i) => (
                <React.Fragment key={i}>
                  <div className={`flex items-center gap-2 px-3 py-2 border rounded-none text-xs font-mono uppercase ${
                    dealingStep === i + 1
                      ? 'bg-zinc-700 text-white border-zinc-700'
                      : dealingStep > i + 1
                        ? 'bg-zinc-100 text-zinc-700 border-zinc-300'
                        : 'bg-white text-zinc-400 border-zinc-200'
                  }`}>
                    <span className="w-5 h-5 flex items-center justify-center rounded-full text-[10px] border border-current">
                      {dealingStep > i + 1 ? '\u2713' : i + 1}
                    </span>
                    <span className="hidden sm:inline">{step}</span>
                  </div>
                  {i < 3 && <div className={`flex-1 h-px ${dealingStep > i + 1 ? 'bg-zinc-500' : 'bg-zinc-200'}`} />}
                </React.Fragment>
              ))}
            </div>

            {/* Step 1: Select Market */}
            {dealingStep === 1 && (
              <div>
                <p className="text-sm text-zinc-600 mb-4">Choose the market you want to invest in.</p>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {['Kenya', 'Uganda', 'Tanzania', 'Nigeria'].map(market => (
                    <button
                      key={market}
                      onClick={() => setDealingForm(prev => ({ ...prev, market }))}
                      className={`p-4 border rounded-none text-sm font-mono transition-colors ${
                        dealingForm.market === market
                          ? 'bg-zinc-700 text-white border-zinc-700'
                          : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50'
                      }`}
                    >
                      {market}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Choose Instrument */}
            {dealingStep === 2 && (
              <div>
                <p className="text-sm text-zinc-600 mb-4">Select an instrument in {dealingForm.market || 'your chosen market'}.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {demoData.markets
                    .filter(m => m.market === dealingForm.market)
                    .map((m, i) => (
                    <button
                      key={i}
                      onClick={() => setDealingForm(prev => ({ ...prev, instrument: m.tenor, rate: m.rate.toString() }))}
                      className={`p-4 border rounded-none text-left transition-colors ${
                        dealingForm.instrument === m.tenor
                          ? 'bg-zinc-700 text-white border-zinc-700'
                          : 'border-zinc-200 hover:bg-zinc-50'
                      }`}
                    >
                      <p className="font-mono text-xs uppercase">{m.tenor}</p>
                      <p className={`text-lg font-bold mt-1 ${dealingForm.instrument === m.tenor ? 'text-white' : 'text-black'}`}>{m.rate.toFixed(2)}%</p>
                      <p className={`text-xs mt-1 ${dealingForm.instrument === m.tenor ? 'text-zinc-300' : 'text-zinc-500'}`}>Last: {formatDate(m.lastAuction)}</p>
                    </button>
                  ))}
                </div>
                {!dealingForm.market && (
                  <p className="text-xs text-zinc-400 mt-4">Please go back and select a market first.</p>
                )}
              </div>
            )}

            {/* Step 3: Amount & Rate */}
            {dealingStep === 3 && (
              <div>
                <p className="text-sm text-zinc-600 mb-4">Enter your investment amount and desired rate.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1">Amount (USD)</label>
                    <input
                      type="number"
                      value={dealingForm.amount}
                      onChange={(e) => setDealingForm(prev => ({ ...prev, amount: e.target.value }))}
                      placeholder="e.g. 50000"
                      className="w-full px-4 py-2 border border-zinc-200 rounded-none text-sm focus:outline-none focus:border-zinc-400"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1">Bid Rate (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={dealingForm.rate}
                      onChange={(e) => setDealingForm(prev => ({ ...prev, rate: e.target.value }))}
                      placeholder="e.g. 16.25"
                      className="w-full px-4 py-2 border border-zinc-200 rounded-none text-sm focus:outline-none focus:border-zinc-400"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Review & Submit */}
            {dealingStep === 4 && (
              <div>
                <p className="text-sm text-zinc-600 mb-4">Review your deal details before submitting.</p>
                <div className="bg-zinc-50 border border-zinc-200 rounded-none p-4 max-w-xl space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">Market</span>
                    <span className="font-mono font-bold">{dealingForm.market || '\u2014'}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t border-zinc-200 pt-3">
                    <span className="text-zinc-500">Instrument</span>
                    <span className="font-mono font-bold">{dealingForm.instrument || '\u2014'}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t border-zinc-200 pt-3">
                    <span className="text-zinc-500">Amount</span>
                    <span className="font-mono font-bold">{dealingForm.amount ? formatCurrency(Number(dealingForm.amount)) : '\u2014'}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t border-zinc-200 pt-3">
                    <span className="text-zinc-500">Bid Rate</span>
                    <span className="font-mono font-bold">{dealingForm.rate ? `${dealingForm.rate}%` : '\u2014'}</span>
                  </div>
                </div>
                <button
                  className={`mt-4 px-6 py-2 font-mono text-xs uppercase tracking-wider rounded-none transition-colors ${
                    bidSubmitting
                      ? 'bg-zinc-400 text-zinc-200 cursor-wait'
                      : 'bg-zinc-700 text-white hover:bg-zinc-800'
                  }`}
                  disabled={bidSubmitting}
                  onClick={handleSubmitBid}
                >
                  {bidSubmitting ? 'Submitting...' : 'Submit Bid'}
                </button>
                {bidError && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-none">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> {bidError}
                  </div>
                )}
                {bidSuccess && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-none">
                    <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" /> {bidSuccess}
                  </div>
                )}
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-zinc-200">
              <button
                onClick={() => setDealingStep(prev => Math.max(1, prev - 1))}
                disabled={dealingStep === 1}
                className={`px-4 py-2 border border-zinc-200 rounded-none text-sm font-mono uppercase transition-colors ${
                  dealingStep === 1 ? 'text-zinc-300 cursor-not-allowed' : 'text-zinc-600 hover:bg-zinc-50'
                }`}
              >
                Back
              </button>
              {dealingStep < 4 && (
                <button
                  onClick={() => setDealingStep(prev => Math.min(4, prev + 1))}
                  className="px-4 py-2 bg-zinc-700 text-white rounded-none text-sm font-mono uppercase hover:bg-zinc-800 transition-colors"
                >
                  Next
                </button>
              )}
            </div>
          </div>

          {/* Demo Lifecycle Tracker */}
          {demoLifecycle && (
            <div className="bg-white border border-zinc-200 rounded-none p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Transaction Lifecycle</h3>
                <span className="font-mono text-[10px] text-zinc-400">{demoLifecycle.csdRef}</span>
              </div>
              <div className="flex items-center gap-1 overflow-x-auto pb-2">
                {DEMO_LIFECYCLE_STEPS.map((step, i) => {
                  const isCurrent = demoLifecycle.currentStep === i;
                  const isComplete = demoLifecycle.currentStep > i;
                  return (
                    <React.Fragment key={step.state}>
                      <div className={`flex items-center gap-1.5 px-2.5 py-1.5 border rounded-none text-[9px] font-mono uppercase whitespace-nowrap ${
                        isComplete
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                          : isCurrent
                            ? 'bg-zinc-700 border-zinc-700 text-white'
                            : 'bg-white border-zinc-200 text-zinc-400'
                      }`}>
                        {isComplete ? <CheckCircle className="w-3 h-3" /> : isCurrent ? <Clock className="w-3 h-3 animate-pulse" /> : null}
                        {step.label}
                      </div>
                      {i < DEMO_LIFECYCLE_STEPS.length - 1 && (
                        <div className={`w-4 h-px flex-shrink-0 ${isComplete ? 'bg-emerald-300' : 'bg-zinc-200'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============================================ */}
      {/* FX TAB                                       */}
      {/* ============================================ */}
      {activeTab === 'fx' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <StatCard label="Active RFQs" value={demoData.fxRfqs.filter(r => r.status === 'open').length} icon={ArrowRightLeft} trend="Awaiting quotes" />
            <StatCard label="Completed" value={demoData.fxRfqs.filter(r => r.status === 'completed').length} icon={CheckCircle} trend="Rate locked" />
            <StatCard label="Best Rate" value="129.85" icon={DollarSign} trend="USD/KES (3 banks)" />
          </div>

          {/* Rates Board */}
          <RatesBoard />

          {/* Post FX RFQ Form */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Post Foreign Exchange RFQ</h3>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1">Currency Pair</label>
                <select className="w-full px-3 py-2 border border-zinc-200 rounded-none text-sm focus:outline-none focus:border-zinc-400">
                  <option>USD/KES</option>
                  <option>EUR/KES</option>
                  <option>GBP/UGX</option>
                  <option>USD/TZS</option>
                  <option>USD/NGN</option>
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1">Direction</label>
                <select className="w-full px-3 py-2 border border-zinc-200 rounded-none text-sm focus:outline-none focus:border-zinc-400">
                  <option>Buy Local Currency</option>
                  <option>Sell Local Currency</option>
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1">Amount (USD)</label>
                <input type="number" placeholder="50000" className="w-full px-3 py-2 border border-zinc-200 rounded-none text-sm focus:outline-none focus:border-zinc-400" />
              </div>
              <div className="flex items-end">
                <button className="w-full px-4 py-2 bg-zinc-700 text-white font-mono text-xs uppercase tracking-wider rounded-none hover:bg-zinc-800 transition-colors">
                  Send RFQ
                </button>
              </div>
            </div>
          </div>

          {/* Active RFQs Table */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Your Foreign Exchange RFQs</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">RFQ ID</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Pair</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Amount</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Direction</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Banks Invited</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Best Rate</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Status</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {demoData.fxRfqs.map(rfq => (
                    <tr key={rfq.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 font-mono text-xs">{rfq.id}</td>
                      <td className="py-3 px-4 font-mono text-xs font-bold">{rfq.pair}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs">{formatCurrency(rfq.amount)}</td>
                      <td className="py-3 px-4 text-xs text-zinc-500">{rfq.direction}</td>
                      <td className="py-3 px-4 font-mono text-xs">{rfq.banksInvited} banks invited</td>
                      <td className="py-3 px-4 text-right font-mono text-xs font-bold">{rfq.bestRate || '\u2014'}</td>
                      <td className="py-3 px-4">
                        <span {...getStatusBadge(rfq.status)}>{rfq.status}</span>
                      </td>
                      <td className="py-3 px-4">
                        {rfq.status === 'open' && rfq.bestRate && (
                          <button className="px-2 py-1 border border-zinc-200 rounded-none text-[10px] font-mono uppercase hover:bg-zinc-50 transition-colors">
                            Accept Best
                          </button>
                        )}
                        {rfq.status !== 'open' && (
                          <button className="flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-none text-[10px] font-mono uppercase hover:bg-zinc-50 transition-colors">
                            <Eye className="w-3 h-3" /> View
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* COMPLIANCE TAB (pillar 3 placeholder)        */}
      {/* ============================================ */}
      {activeTab === 'compliance' && (
        <ComplianceTab role="investor_individual" />
      )}

      {/* ============================================ */}
      {/* FORWARDS MARKET TAB                          */}
      {/* ============================================ */}
      {activeTab === 'forwards_market' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <StatCard label="Active Forwards" value={demoData.forwardsPool.filter(f => f.status === 'active').length} icon={Globe} trend="With divestment rate" />
            <StatCard label="Pending Divestment RFQ" value={demoData.forwardsPool.filter(f => f.status === 'pending').length} icon={Clock} trend="Awaiting quote" />
            <StatCard label="Spot Rate (USD/KES)" value="129.50" icon={TrendingUp} trend="Forward curve reference" />
          </div>

          {/* Divestment Rate RFQ */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Request Divestment Rate</h3>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1">Currency Pair</label>
                <select className="w-full px-3 py-2 border border-zinc-200 rounded-none text-sm focus:outline-none focus:border-zinc-400">
                  <option>USD/KES</option>
                  <option>EUR/KES</option>
                  <option>GBP/UGX</option>
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1">Tenor</label>
                <select className="w-full px-3 py-2 border border-zinc-200 rounded-none text-sm focus:outline-none focus:border-zinc-400">
                  <option>1M</option>
                  <option>3M</option>
                  <option>6M</option>
                  <option>9M</option>
                  <option>12M</option>
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1">Notional (USD)</label>
                <input type="number" placeholder="50000" className="w-full px-3 py-2 border border-zinc-200 rounded-none text-sm focus:outline-none focus:border-zinc-400" />
              </div>
              <div className="flex items-end">
                <button className="w-full px-4 py-2 bg-zinc-700 text-white font-mono text-xs uppercase tracking-wider rounded-none hover:bg-zinc-800 transition-colors">
                  Request Quote
                </button>
              </div>
            </div>
          </div>

          {/* Forwards Pool */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Forwards Pool Status</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">ID</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Pair</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Notional</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Tenor</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Divestment Rate</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Maturity</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {demoData.forwardsPool.map(fwd => (
                    <tr key={fwd.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 font-mono text-xs">{fwd.id}</td>
                      <td className="py-3 px-4 font-mono text-xs font-bold">{fwd.pair}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs">{formatCurrency(fwd.notional)}</td>
                      <td className="py-3 px-4 font-mono text-xs">{fwd.tenor}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs font-bold">{fwd.divestmentRate || '\u2014'}</td>
                      <td className="py-3 px-4 text-xs text-zinc-500">{formatDate(fwd.maturity)}</td>
                      <td className="py-3 px-4">
                        <span {...getStatusBadge(fwd.status)}>{fwd.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Forward Curve Display */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">USD/KES Forward Curve</h3>
            <div className="grid grid-cols-6 gap-2">
              {demoData.forwardCurve.map((pt, i) => (
                <div key={i} className="border border-zinc-200 rounded-none p-3 text-center">
                  <p className="font-mono text-[10px] uppercase text-zinc-500">{pt.tenor}</p>
                  <p className="text-lg font-bold text-black mt-1">{pt.rate.toFixed(2)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Hedge Coverage KPI */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-4 h-4 text-zinc-400" />
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Hedge Coverage</h3>
            </div>
            <p className="font-mono text-2xl font-bold text-black">
              {(() => {
                const totalNotional = demoData.forwardsPool.reduce((s, f) => s + f.notional, 0);
                const totalExposure = totalNotional * 1.4;
                return totalExposure > 0 ? `${((totalNotional / totalExposure) * 100).toFixed(0)}%` : '\u2014';
              })()}
            </p>
            <p className="font-mono text-[10px] text-zinc-400 mt-0.5">hedged notional / total local-currency exposure</p>
          </div>

          {/* FX Overlay */}
          <FxOverlayTab positions={portfolioPositions} role="investor_individual" fxHedges={fxHedges} />
        </div>
      )}

      {/* ============================================ */}
      {/* SETTLEMENT TAB                               */}
      {/* ============================================ */}
      {activeTab === 'settlement' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <StatCard label="Pending Settlements" value={demoData.settlements.filter(s => s.status === 'pending_settlement').length} icon={Clock} trend="Awaiting confirmation" />
            <StatCard label="Completed" value={demoData.settlements.filter(s => s.status === 'completed').length} icon={CheckCircle} trend="Settled this month" />
            <StatCard label="Total Fees" value={formatCurrency(demoData.settlements.reduce((s, t) => s + t.fees, 0))} icon={CreditCard} trend="Platform + bank fees" />
          </div>

          {/* Settlement Summary */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Settlement Summary</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">ID</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Instrument</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Face Value</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Foreign Exchange Rate</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Fees</th>
                    <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Total</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Date</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Status</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {demoData.settlements.map(set => (
                    <tr key={set.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 font-mono text-xs">{set.id}</td>
                      <td className="py-3 px-4 font-mono text-xs font-bold">{set.instrument}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs">{formatCurrency(set.faceValue)}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs">{set.fxRate.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs">${set.fees.toFixed(2)}</td>
                      <td className="py-3 px-4 text-right font-mono text-xs font-bold">{formatCurrency(set.total)}</td>
                      <td className="py-3 px-4 text-xs text-zinc-500">{formatDate(set.date)}</td>
                      <td className="py-3 px-4">
                        <span {...getStatusBadge(settlementStatuses[set.id] || set.status)}>{(settlementStatuses[set.id] || set.status).replace(/_/g, ' ')}</span>
                      </td>
                      <td className="py-3 px-4">
                        {(settlementStatuses[set.id] || set.status) === 'pending_settlement' && (
                          <div className="flex items-center gap-2">
                            <label className="flex items-center gap-1 text-[10px] font-mono uppercase text-zinc-500">
                              <input
                                type="checkbox"
                                checked={settlementAgreed[set.id] || false}
                                onChange={() => setSettlementAgreed(prev => ({ ...prev, [set.id]: !prev[set.id] }))}
                                className="rounded-none"
                              />
                              I agree
                            </label>
                            <button
                              onClick={() => handleSettle(set)}
                              disabled={!settlementAgreed[set.id] || settleSubmitting === set.id}
                              className={`px-2 py-1 rounded-none text-[10px] font-mono uppercase transition-colors ${
                                settlementAgreed[set.id] && settleSubmitting !== set.id
                                  ? 'bg-zinc-700 text-white hover:bg-zinc-800'
                                  : 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                              }`}
                            >
                              {settleSubmitting === set.id ? '...' : 'Settle'}
                            </button>
                          </div>
                        )}
                        {set.status === 'completed' && (
                          <button className="flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-none text-[10px] font-mono uppercase hover:bg-zinc-50 transition-colors">
                            <FileText className="w-3 h-3" /> Receipt
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* PORTFOLIO TAB                                */}
      {/* ============================================ */}
      {activeTab === 'portfolio' && (
        <PortfolioPage role="investor_individual" />
      )}

      {/* ============================================ */}
      {/* SECONDARY MARKET TAB                         */}
      {/* ============================================ */}
      {activeTab === 'secondary_market' && (
        <LiquidityTab role="investor_individual" />
      )}

      {/* ============================================ */}
      {/* PROJECTIONS TAB                              */}
      {/* ============================================ */}
      {activeTab === 'projections' && (
        <ProjectionsPage role="investor_individual" />
      )}

      {/* ============================================ */}
      {/* PROFILE TAB                                  */}
      {/* ============================================ */}
      {activeTab === 'profile' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <StatCard label="KYC Status" value={demoData.kycStatus.filter(k => k.kycStatus === 'verified').length + '/' + demoData.kycStatus.length} icon={Shield} trend="Banks verified" />
            <StatCard label="Documents" value={demoData.documents.length} icon={FileText} trend={demoData.documents.filter(d => d.status === 'verified').length + ' verified'} />
            <StatCard label="CDS Cards" value={demoData.kycStatus.filter(k => k.cdsCard === 'active').length + ' active'} icon={CreditCard} trend="Depository access" />
          </div>

          {/* KYC Status per Bank */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">KYC Status per Bank</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Bank</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">KYC Status</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">CDS Card</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Last Updated</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {demoData.kycStatus.map((k, i) => (
                    <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 text-xs font-bold">{k.bank}</td>
                      <td className="py-3 px-4">
                        <span {...getStatusBadge(k.kycStatus)}>{k.kycStatus.replace(/_/g, ' ')}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span {...getStatusBadge(k.cdsCard)}>{k.cdsCard}</span>
                      </td>
                      <td className="py-3 px-4 text-xs text-zinc-500">{formatDate(k.lastUpdated)}</td>
                      <td className="py-3 px-4">
                        <button className="flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-none text-[10px] font-mono uppercase hover:bg-zinc-50 transition-colors">
                          <Eye className="w-3 h-3" /> View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Documents */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Documents</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Document</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Type</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Status</th>
                    <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Uploaded</th>
                  </tr>
                </thead>
                <tbody>
                  {demoData.documents.map((doc, i) => (
                    <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-4 text-xs font-bold">{doc.name}</td>
                      <td className="py-3 px-4 font-mono text-xs text-zinc-500">{doc.type}</td>
                      <td className="py-3 px-4">
                        <span {...getStatusBadge(doc.status)}>{doc.status}</span>
                      </td>
                      <td className="py-3 px-4 text-xs text-zinc-500">{formatDate(doc.uploadedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Consents */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">Data Consents</h3>
            <div className="space-y-3">
              {[
                { label: 'Share KYC data with partner banks', granted: true },
                { label: 'Allow portfolio data for credit assessment', granted: true },
                { label: 'Marketing communications', granted: false },
                { label: 'Anonymous data for market analytics', granted: true },
              ].map((consent, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                  <span className="text-sm text-zinc-700">{consent.label}</span>
                  <span {...getStatusBadge(consent.granted ? 'active' : 'pending')}>
                    {consent.granted ? 'Granted' : 'Not Granted'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MESSAGES TAB                                 */}
      {/* ============================================ */}
      {activeTab === 'messages' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <StatCard label="Unread" value={unreadMessages} icon={MessageSquare} trend="Notifications" />
            <StatCard label="Total Messages" value={demoData.messages.length} icon={Bell} trend="All time" />
            <StatCard label="Lifecycle Updates" value={demoData.messages.filter(m => m.type === 'lifecycle').length} icon={CheckCircle} trend="Trade events" />
          </div>

          {/* Message List */}
          <div className="bg-white border border-zinc-200 rounded-none p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Notifications</h3>
              <button className="px-3 py-1 border border-zinc-200 rounded-none text-[10px] font-mono uppercase hover:bg-zinc-50 transition-colors">
                Mark All Read
              </button>
            </div>
            <div className="space-y-2">
              {demoData.messages.map(msg => (
                <div
                  key={msg.id}
                  className={`p-4 border rounded-none transition-colors ${
                    msg.read ? 'border-zinc-100 bg-white' : 'border-zinc-300 bg-zinc-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">
                        {msg.type === 'lifecycle' && <CheckCircle className="w-4 h-4 text-zinc-500" />}
                        {msg.type === 'settlement' && <CreditCard className="w-4 h-4 text-zinc-500" />}
                        {msg.type === 'fx' && <ArrowRightLeft className="w-4 h-4 text-zinc-500" />}
                        {msg.type === 'maturity' && <Calendar className="w-4 h-4 text-zinc-500" />}
                        {msg.type === 'kyc' && <Shield className="w-4 h-4 text-zinc-500" />}
                        {msg.type === 'secondary' && <Building2 className="w-4 h-4 text-zinc-500" />}
                      </div>
                      <div>
                        <p className={`text-sm ${msg.read ? 'text-zinc-600' : 'text-black font-bold'}`}>{msg.title}</p>
                        <p className="text-xs text-zinc-500 mt-1">{msg.body}</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-4">
                      <p className="text-[10px] font-mono text-zinc-400">{formatTime(msg.date)}</p>
                      {!msg.read && (
                        <span className="inline-block w-2 h-2 rounded-full bg-zinc-700 mt-1" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndividualInvestorDashboard;
